import { CommonModule, Location, formatDate } from '@angular/common';
import { HttpParams } from '@angular/common/http';
import { Component, inject, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { FormGroup, FormControl, FormsModule, ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '@app/core/api/auth/auth.service';
import { NotificationService } from '@app/core/api/notification/notification.service';
import {
    InvestListResponse,
    InvestNarrativeResponse,
    InvestService,
    InvestUserRequest,
} from '@app/core/api/users/invest.service';
import { EchoService } from '@app/core/services/echo.service';
import { feedbackFormValidationMap } from '@app/helpers/validation-maps/feedback-form.map';
import { isFieldInvalid, getFieldError } from '@app/helpers/validators/custom-form-validation';
import { ModalService } from '@app/shared/components/modal/modal.service';
import { ToastService } from '@app/shared/components/toast/toast.service';
import { ButtonComponent } from '@app/shared/forms/button/button.component';
import { ComboBoxComponent, ComboBoxOption } from '@app/shared/forms/combo-box/combo-box.component';
import { InputComponent } from '@app/shared/forms/input/input.component';
import { QuillEditorComponent } from '@app/shared/forms/quill-editor/quill-editor.component';
import {
    Mail,
    BellOff,
    LucideAngularModule,
    UserRoundMinus,
    UserRoundX,
    UserRoundPlus,
    BellRing,
    UserCheck,
    Loader,
    CircleAlert,
} from 'lucide-angular';
import { Subject, takeUntil } from 'rxjs';

@Component({
    selector: 'app-connect-user',
    imports: [
        InputComponent,
        ButtonComponent,
        QuillEditorComponent,
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        LucideAngularModule,
        ComboBoxComponent,
    ],
    templateUrl: './connect-user.component.html',
    styleUrl: './connect-user.component.scss',
})
export class ConnectUserComponent implements OnInit, OnDestroy {
    authService = inject(AuthService);
    investService = inject(InvestService);
    modalService = inject(ModalService);
    toastService = inject(ToastService);
    echoService = inject(EchoService);
    notificationService = inject(NotificationService);
    private router = inject(Router);
    private fb = inject(FormBuilder);

    readonly Mail = Mail;
    readonly UserCheck = UserCheck;
    readonly UserRoundX = UserRoundX;
    readonly UserRoundMinus = UserRoundMinus;
    readonly UserRoundPlus = UserRoundPlus;
    readonly BellRing = BellRing;
    readonly BellOff = BellOff;
    readonly Loader = Loader;
    readonly CircleAlert = CircleAlert;

    @ViewChild('connectionModal') connectionModal!: TemplateRef<any>;
    @ViewChild('requestModal') requestModal!: TemplateRef<any>;
    @ViewChild('pendingRequestModal') pendingRequestModal!: TemplateRef<any>;
    @ViewChild('connectionDisconnectModal') connectionDisconnectModal!: TemplateRef<any>;
    @ViewChild('assignModal') assignModal!: TemplateRef<any>;
    @ViewChild('feedbackModal') feedbackModal!: TemplateRef<any>;
    @ViewChild('confirmFeedbackModal') confirmFeedbackModal!: TemplateRef<any>;
    @ViewChild('muteModal') muteModal!: TemplateRef<any>;

    type: 'Investor' | 'Investee' = 'Investor';
    status: 'pending' | 'accepted' | 'cancelled' | 'declined' | 'disconnect' | undefined;
    isReceiver: boolean = false;
    isMuted: boolean = false;
    isConnected: boolean = false;
    nickname: string = '';
    tempNickname: string = '';

    Math = Math;
    id: number = 0;
    chatId: number = 0;
    code: string = '';
    content: string = '';
    lastUpdated: string = '';

    investorId: string = '';
    investeeId: string = '';
    receiverId: string = '';

    destroy$ = new Subject<void>();

    form = new FormGroup({
        content: new FormControl(''),
    });

    isSubmitted = false;
    feedbackForm: FormGroup;
    showOthersNo = false;

    transaction: ComboBoxOption[] = [
        {
            label: 'Merger',
            value: 'Merger',
        },
        {
            label: 'Consolidation',
            value: 'Consolidation',
        },
        {
            label: 'Acquisition',
            value: 'Acquisition',
        },
    ];

    reasons: ComboBoxOption[] = [
        {
            label: 'Conflicting business models or goals',
            value: 'Conflicting business models or goals',
        },
        {
            label: 'Overvaluation concerns',
            value: 'Overvaluation concerns',
        },
        {
            label: 'Unsatisfactory due diligence outcomes',
            value: 'Unsatisfactory due diligence outcomes',
        },
        {
            label: 'Market instability or Industry disruption',
            value: 'Market instability or Industry disruption',
        },
        {
            label: 'No synergy or added value',
            value: 'No synergy or added value',
        },
        {
            label: 'Others',
            value: 'Others',
        },
    ];

    stage: ComboBoxOption[] = [
        {
            label: 'Exploratory discussions',
            value: 'Exploratory discussions',
        },
        {
            label: 'Due diligence in progress',
            value: 'Due diligence in progress',
        },
        {
            label: 'Drafting agreements',
            value: 'Drafting agreements',
        },
        {
            label: 'Definitive agreements signed',
            value: 'Definitive agreements signed',
        },
        {
            label: 'Regulatory approvals being secured',
            value: 'Regulatory approvals being secured',
        },
        {
            label: 'Completed/with regulatory approval',
            value: 'Completed/with regulatory approval',
        },
        {
            label: 'On hold',
            value: 'On hold',
        },
        {
            label: 'Terminated',
            value: 'Terminated',
        },
        {
            label: 'Others',
            value: 'Others',
        },
    ];

    assignLoading = false;
    loading: boolean = false;
    profileLoading = true;
    isSubmitting: boolean = false;
    isOwner: boolean = false;
    requestData: any;

    readonly currentUser = this.authService.user();

    get actionsDisabled(): boolean {
        return this.loading || this.profileLoading;
    }
    userProfile: any = [];

    profileUpdateRequired: boolean = false;
    lastProfileUpdated: string | null = null;
    nextUpdateDue: string | null = null;

    constructor(private route: ActivatedRoute, private location: Location) {
        this.feedbackForm = this.fb.group({
            transactionQuestion: ['', Validators.required],
            transaction: [''],
            reasons: [[]],
            othersNo: ['', [Validators.maxLength(255)]],
            stage: ['', Validators.required],
            stage_others: ['', Validators.maxLength(255)],
        });
    }

    ngOnInit() {
        const open = this.route.snapshot.data['openFeedback'];
        const params = this.route.snapshot.queryParams;
        const urlData = this.investService.getState();

        this.id = urlData?.id ?? params['id'];
        this.chatId = urlData?.chatId ?? null;
        this.code = urlData?.code ?? null;
        this.type = urlData?.type ?? 'Investor';
        this.status = urlData?.status ?? null;
        this.investorId = urlData?.investorId ?? params['investorId'];
        this.investeeId = urlData?.investeeId ?? params['investeeId'];
        this.nickname = urlData?.nickname ?? null;

        const isInvestorUser = this.currentUser?.type == 'Investor' && this.currentUser?.invest_id == this.investorId;
        const isInvesteeUser = this.currentUser?.type == 'Investee' && this.currentUser?.invest_id == this.investeeId;

        if (!isInvestorUser && !isInvesteeUser) {
            this.router.navigate(['/forbidden']);
            return;
        }

        if (open) {
            this.location.replaceState('/connect-with-users/feedback');

            this.investService.setState({
                id: this.id,
                investorId: this.investorId,
                investeeId: this.investeeId,
            });
        }

        this.loadRequests(open);
        this.loadUserNarrative();

        this.form.disable();

        this.setupConditionalValidators();
    }

    setupConditionalValidators() {
        this.feedbackForm.get('transactionQuestion')?.valueChanges.subscribe((value) => {
            const reasons = this.feedbackForm.get('reasons');
            const transaction = this.feedbackForm.get('transaction');

            if (value === 'no') {
                reasons?.addValidators(Validators.required);
                transaction?.removeValidators(Validators.required);
                transaction?.setValue('');
            } else if (value === 'yes') {
                transaction?.addValidators(Validators.required);
                reasons?.removeValidators(Validators.required);
                reasons?.setValue([]);
            } else {
                reasons?.removeValidators(Validators.required);
                transaction?.removeValidators(Validators.required);
            }

            reasons?.updateValueAndValidity();
            transaction?.updateValueAndValidity();
        });

        this.feedbackForm.get('reasons')?.valueChanges.subscribe((value: string[]) => {
            const othersNo = this.feedbackForm.get('othersNo');
            const hasOthers = value?.includes('Others');

            if (hasOthers) {
                othersNo?.addValidators(Validators.required);
            } else {
                othersNo?.removeValidators(Validators.required);
                othersNo?.setValue('');
            }

            othersNo?.updateValueAndValidity();
        });

        this.feedbackForm.get('stage')?.valueChanges.subscribe((value) => {
            const stageOthers = this.feedbackForm.get('stage_others');

            if (value === 'Others') {
                stageOthers?.addValidators(Validators.required);
            } else {
                stageOthers?.removeValidators(Validators.required);
                stageOthers?.setValue('');
            }

            stageOthers?.updateValueAndValidity();
        });
    }

    loadRequests(open: any) {
        this.loading = true;
        let params = new HttpParams();

        params = params.set('investor_id', this.investorId);
        params = params.set('investee_id', this.investeeId);

        this.investService
            .getAllRequests(params)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res: InvestListResponse) => {
                    const invest = res.data[0];
                    this.requestData = invest;

                    if (open) {
                        setTimeout(() => this.feedback(this.requestData), 200);
                    }

                    // this.code = this.type == 'Investor' ? invest?.investor?.corporate_name : invest?.investee?.code;
                    if (invest) {
                        this.code = this.type == 'Investor' ? invest?.investor?.corporate_name : invest?.investee?.code;
                    }
                    this.status = invest?.status_id ?? null;
                    this.isReceiver = this.currentUser?.invest_id == invest?.receiver?.toString();
                    this.isMuted = this.type == 'Investor' ? invest?.investee_muted : invest?.investor_muted;
                    this.nickname = invest?.nickname;

                    this.isConnected = this.status == 'accepted';
                    this.loading = false;
                },
                error: () => {
                    this.loading = false;
                },
            });
    }

    loadUserNarrative() {
        this.profileLoading = true;

        this.investService
            .getInvestProfile(Number(this.currentUser?.type == 'Investor' ? this.investeeId : this.investorId))
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (res: InvestNarrativeResponse) => {
                    this.userProfile = res.data;
                    this.isOwner = this.currentUser?.invest_id == res.data.invest_entity_id;

                    this.lastUpdated = formatDate(this.userProfile?.updated_at, 'MMMM dd, yyyy', 'en-US', 'UTC') ?? '';
                    this.form.get('content')?.setValue(this.userProfile?.content ?? 'NO PUBLIC PROFILE');
                    if (!this.isOwner) {
                        this.form.get('content')?.disable();
                    }

                    this.code =
                        this.userProfile?.type == 'Investor'
                            ? this.userProfile?.corporate_name
                            : this.userProfile?.investee_code;

                    this.profileLoading = false;
                },
                error: () => {
                    this.form.get('content')?.setValue('NO PUBLIC PROFILE');
                    this.profileLoading = false;
                },
            });
    }

    connect() {
        const request: Partial<InvestUserRequest> = {
            investor_id: this.investorId,
            investee_id: this.investeeId,
            receiver_id: this.type == 'Investor' ? this.investorId : this.investeeId,
            status_id: 'pending',
        };

        const modal = this.modalService.open({
            title: `Connection Request`,
            contentTemplate: this.requestModal,
            contentData: {
                onConfirm: () => {
                    this.loading = true;
                    this.isSubmitting = true;
                    this.investService
                        .connectUser(request)
                        .pipe(takeUntil(this.destroy$))
                        .subscribe({
                            next: (response) => {
                                this.toastService.show({
                                    title: 'Success',
                                    content: response.message ?? `The request to connect has been sent to ${this.code}`,
                                    type: 'success',
                                    duration: 5000,
                                });
                                this.modalService.close(modal);
                                this.loading = false;
                                this.isSubmitting = false;
                                this.loadRequests(false);
                            },
                            error: (error) => {
                                this.toastService.show({
                                    title: 'Error',
                                    content: `Something went wrong. Please try again later.`,
                                    type: 'error',
                                    duration: 5000,
                                });
                                this.loading = false;
                                this.isSubmitting = false;
                                this.modalService.close(modal);
                            },
                        });
                },
                onCancel: () => {
                    this.modalService.close(modal);
                    this.loading = false;
                },
            },
            showCloseButton: false,
            backdropDismiss: false,
            keyboardClose: false
        });
    }

    disconnect(value: string, request: Partial<InvestUserRequest>) {
        this.loading = true;
        this.investService
            .disconnect(request)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.toastService.show({
                        title: 'Success',
                        content: `${this.type} ${value} successfully`,
                        type: 'success',
                        duration: 5000,
                    });
                    this.investService.clearState();
                    this.router.navigate(['investor-investee-connect']);
                    this.loading = false;
                },
                error: () => {
                    this.loading = false;
                },
            });
    }

    cancel() {
        const request: Partial<InvestUserRequest> = {
            investor_id: this.investorId,
            investee_id: this.investeeId,
            receiver_id: this.currentUser?.invest_id == this.investorId ? this.investeeId : this.investorId,
            status_id: 'cancelled'
        };
        const connectionModal = this.modalService.open({
            title: 'Cancel Connect Request',
            contentTemplate: this.connectionModal,
            contentData: {
                onConfirmRequest: () => {
                    this.disconnect('request cancelled', request);
                    this.modalService.close(connectionModal);
                },
                onClose: () => {
                    this.modalService.close(connectionModal);
                },
            },
            position: 'center',
            animation: 'fade',
            showCloseButton: false,
            backdropDismiss: false,
            keyboardClose: false
        });
    }

    revoke() {
        const request: Partial<InvestUserRequest> = {
            investor_id: this.investorId,
            investee_id: this.investeeId,
            receiver_id: this.currentUser?.invest_id == this.investorId ? this.investeeId : this.investorId,
            status_id: 'disconnect'
        };
        const connectionDisconnectModal = this.modalService.open({
            title: 'Disconnection Request',
            contentTemplate: this.connectionDisconnectModal,
            contentData: {
                onConfirmRequest: () => {
                    this.disconnect('disconnected', request);
                    this.modalService.close(connectionDisconnectModal);
                },
                onCancel: () => {
                    this.modalService.close(connectionDisconnectModal);
                },
            },
            position: 'center',
            animation: 'fade',
            showCloseButton: false,
            backdropDismiss: false,
            keyboardClose: false
        });
    }

    message() {
        this.router.navigate(['chat'], { queryParams: { chatId: this.chatId } });
    }

    assign() {
        console.log('assign nickname');
        this.tempNickname = this.nickname;

        const modalRef = this.modalService.open({
            title: `Change ${this.type} nickname`,
            contentTemplate: this.assignModal,
            contentData: {
                loading: () => this.assignLoading,

                onConfirm: (nickname: string) => {
                    const trimmed = nickname.trim();

                    if (!trimmed) {
                        this.toastService.show({
                            title: 'Error',
                            content: 'Nickname is required.',
                            duration: 3000,
                            position: 'right',
                            type: 'error',
                        });
                        return;
                    }
                    if (trimmed.length < 2) {
                        this.toastService.show({
                            title: 'Error',
                            content: 'Nickname must be at least 2 characters.',
                            duration: 3000,
                            position: 'right',
                            type: 'error',
                        });
                        return;
                    }

                    this.assignLoading = true;

                    const formData = new FormData();
                    formData.append('nickname', trimmed);

                    this.addNickname(trimmed, modalRef);
                },

                onCancel: () => {
                    this.tempNickname = '';
                },
            },
            position: 'center',
            animation: 'fade',
            showCloseButton: false,
            backdropDismiss: false,
            keyboardClose: false
        });
    }

    addNickname(nickname: string, modal: any) {
        this.loading = true;

        const request: Partial<InvestUserRequest> = {
            investor_id: this.investorId,
            investee_id: this.investeeId,
            nickname: nickname,
        };
        console.info('connection request: ', request);

        this.investService
            .addNickname(request)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.modalService.close(modal);
                    this.toastService.show({
                        title: 'Success',
                        content: ` ${this.type} nickname updated successfully`,
                        type: 'success',
                        duration: 5000,
                    });

                    this.loading = false;
                    this.assignLoading = false;
                    this.loadRequests(false);
                },
                error: () => {
                    this.loading = false;
                },
            });
    }

    mute() {
        const request: Partial<InvestUserRequest> = {
            investor_id: this.investorId,
            investee_id: this.investeeId,
            muted_by: this.currentUser?.invest_id,
        };

        const modal = this.modalService.open({
            title: `Mute ${this.type}`,
            contentTemplate: this.muteModal,
            contentData: {
                onConfirm: () => {
                    this.isSubmitting = true;
                    this.loading = true;
                    this.investService
                        .mute(request)
                        .pipe(takeUntil(this.destroy$))
                        .subscribe({
                            next: (response) => {
                                this.toastService.show({
                                    title: 'Success',
                                    content: response.message ?? `Notification for request to connect from ${this.code} has been disabled.`,
                                    type: 'success',
                                    duration: 5000,
                                });

                                this.modalService.close(modal);
                                this.loadRequests(false);
                                this.loading = false;
                                this.isSubmitting = false;
                            },
                            error: () => {
                                this.loading = false;
                                this.isSubmitting = false;
                                this.modalService.close(modal);
                            },
                        });
                },
                onCancel: () => {
                    this.modalService.close(modal);
                    this.loading = false;
                },
            },
            showCloseButton: false,
            backdropDismiss: false,
            keyboardClose: false
        });
    }

    unmute() {
        const request: Partial<InvestUserRequest> = {
            investor_id: this.investorId,
            investee_id: this.investeeId,
            muted_by: this.currentUser?.invest_id,
        };

        const modal = this.modalService.open({
            title: `Unmute ${this.type}`,
            contentTemplate: this.muteModal,
            contentData: {
                onConfirm: () => {
                    this.isSubmitting = true;
                    this.loading = true;
                    this.investService
                        .unmute(request)
                        .pipe(takeUntil(this.destroy$))
                        .subscribe({
                            next: (response) => {
                                this.toastService.show({
                                    title: 'Success',
                                    content: `You have unmuted this ${this.type}`,
                                    type: 'success',
                                    duration: 5000,
                                });

                                this.modalService.close(modal);
                                this.loadRequests(false);
                                this.loading = false;
                                this.isSubmitting = false;
                            },
                            error: () => {
                                this.loading = false;
                                this.isSubmitting = false;
                                this.modalService.close(modal);
                            },
                        });
                },
                onCancel: () => {
                    this.modalService.close(modal);
                    this.loading = false;
                },
            },
            showCloseButton: false,
            backdropDismiss: false,
            keyboardClose: false
        });
    }

    feedback(data: any) {
        console.log(data);

        let entity, classification;
        const isInvestor = this.currentUser?.type == 'Investor' && this.currentUser?.invest_id == this.investorId;

        if (isInvestor) {
            entity = data.investee?.code;
            classification = 'Investee';
        } else {
            entity = data.investor?.corporate_name;
            classification = 'Investor';
        }

        const modalRef = this.modalService.open({
            title: `EPIC System Investor - Investee Status Form `,
            contentTemplate: this.feedbackModal,
            size: 'xl',
            contentData: {
                entity,
                classification,
                description: `This survey form is intended to capture the current status of your connection with ${entity} in the EPIC system.We assure you that all your answers are all treated strictly CONFIDENTIAL and for PDIC use only.`,

                onSubmit: () => {
                    this.submit(modalRef, isInvestor);
                },

                onCancel: () => {
                    this.modalService.close(modalRef);
                    this.router.navigate(['investor-investee-connect']);
                },
            },
            position: 'center',
            animation: 'fade',
            showCloseButton: false,
            backdropDismiss: false,
            keyboardClose: false
        });
    }

    submit(modal: number, isInvestor: boolean) {
        this.isSubmitted = true;

        if (this.feedbackForm.invalid) {
            this.feedbackForm.markAllAsTouched();
            return;
        }

        const confirmModal = this.modalService.open({
            title: `Feedback Confirmation`,
            contentTemplate: this.confirmFeedbackModal,
            contentData: {
                onConfirm: () => {
                    this.loading = true;

                    const values = this.feedbackForm.getRawValue();

                    const formData = new FormData();
                    Object.keys(values).forEach((key) => {
                        const value = values[key];
                        if (Array.isArray(value)) {
                            value.forEach((item) => formData.append(key + '[]', item));
                        } else {
                            formData.append(key, value);
                        }
                    });

                    formData.append('feedback_from_entity_id', (this.currentUser?.invest_id ?? null)?.toString() || '');
                    formData.append('feedback_to_entity_id', (isInvestor ? this.investeeId : this.investorId)?.toString() || '');

                    this.investService
                        .feedback(formData)
                        .pipe(takeUntil(this.destroy$))
                        .subscribe({
                            next: (response) => {
                                this.toastService.show({
                                    title: 'Success',
                                    content: response?.message || `Feedback submitted successfully`,
                                    type: 'success',
                                    duration: 3000,
                                });
                                this.feedbackForm.reset();
                                this.modalService.close(modal);
                                this.modalService.close(confirmModal);
                                this.location.replaceState('/connect-with-users');
                            },
                            error: (error) => {
                                console.error('Error creating configuration:', error);
                                this.toastService.show({
                                    title: 'Error',
                                    content: error?.error?.message || `Failed to submit feedback`,
                                    type: 'error',
                                    duration: 5000,
                                });
                                this.loading = false;
                                this.feedbackForm.enable();
                                this.feedbackForm.reset();
                                this.modalService.close(modal);
                                this.modalService.close(confirmModal);
                                this.location.replaceState('/connect-with-users');
                            },
                            complete: () => {
                                if (this.feedbackForm.disabled) {
                                    this.feedbackForm.enable();
                                }
                                this.loading = false;
                            },
                        });
                },
                onCancel: () => {
                    this.modalService.close(confirmModal);
                    this.loading = false;
                },
            },
            showCloseButton: false,
            backdropDismiss: false,
            keyboardClose: false
        });
    }

    toggleOthersNo() {
        const reasons = this.feedbackForm.get('reasons')?.value || [];
        this.showOthersNo = reasons.includes('Others');
    }

    isFieldInvalid(fieldName: string): boolean {
        return isFieldInvalid(this.feedbackForm, fieldName, this.isSubmitted);
    }

    getFieldError(fieldName: string): string | null {
        return getFieldError(this.feedbackForm, fieldName, feedbackFormValidationMap, this.isSubmitted);
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
}
